import React, { useEffect, useRef, TextareaHTMLAttributes } from 'react';
import './AutoResizeTextarea.css';

interface AutoResizeTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    minRows?: number;
    maxRows?: number;
}

const AutoResizeTextarea: React.FC<AutoResizeTextareaProps> = ({ 
    minRows = 1, 
    maxRows,
    value,
    onChange,
    ...props 
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const adjustHeight = () => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        // Reset height to auto to get the correct scrollHeight
        textarea.style.height = 'auto';
        
        // Get computed styles
        const styles = getComputedStyle(textarea);
        const lineHeight = parseFloat(styles.lineHeight);
        const paddingTop = parseFloat(styles.paddingTop);
        const paddingBottom = parseFloat(styles.paddingBottom);
        const borderTop = parseFloat(styles.borderTopWidth);
        const borderBottom = parseFloat(styles.borderBottomWidth);
        
        // Start with scrollHeight
        let newHeight = textarea.scrollHeight;
        
        // Apply min/max constraints if specified
        if (minRows) {
            const minHeight = lineHeight * minRows + paddingTop + paddingBottom + borderTop + borderBottom;
            newHeight = Math.max(newHeight, minHeight);
        }
        
        if (maxRows) {
            const maxHeight = lineHeight * maxRows + paddingTop + paddingBottom + borderTop + borderBottom;
            newHeight = Math.min(newHeight, maxHeight);
        }
        
        textarea.style.height = `${newHeight}px`;
    };

    useEffect(() => {
        adjustHeight();
    }, [value]);

    // Also adjust on mount
    useEffect(() => {
        adjustHeight();
    }, []);

    return (
        <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => {
                adjustHeight();
                if (onChange) {
                    onChange(e);
                }
            }}
            rows={minRows}
            {...props}
            className={`AutoResizeTextarea ${props.className || ''}`}
        />
    );
};

export default AutoResizeTextarea;

